import { useEffect } from 'react';
import { View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Circle } from 'react-native-svg';
import { Stack } from '@/common/components/Stack/Stack';
import { TColors, colors } from '@coverflex-tech/hypernova-design-tokens';
import { styles } from './CircleProgress.styles';

export type TCircleProgressProps = {
  radius: number;
  strokeWidth: number;
  percentage: number;
  children?: React.ReactNode;
  strokeColor?: keyof TColors['light']['semantic']['surface'];
  className?: string;
};

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

export const CircleProgress = ({
  radius,
  strokeWidth,
  percentage,
  children,
  strokeColor = 'accentSecondary40',
  className,
}: TCircleProgressProps) => {
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (percentage / 100);
  const animatedValue = useSharedValue(0);
  const stroke = colors.light.semantic.surface[strokeColor];

  useEffect(() => {
    animatedValue.value = withTiming(offset, {
      duration: 1000,
      easing: Easing.out(Easing.ease),
    });
  }, [offset, animatedValue]);

  const animatedProps = useAnimatedProps(() => {
    return {
      strokeDashoffset: animatedValue.value,
    };
  });

  const width = 2 * radius;
  const height = 2 * radius;
  return (
    <View className={styles.root({ className })} style={{ width, height }}>
      <Svg width={width} height={height} fill="none">
        <AnimatedCircle
          cx={radius}
          cy={radius}
          r={radius - strokeWidth / 2}
          stroke={colors.light.semantic.surface.accentSecondary40}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={animatedValue.value}
          strokeLinecap="round"
          animatedProps={animatedProps}
        />
        <Circle
          cx={radius}
          cy={radius}
          r={radius - strokeWidth / 2}
          stroke={stroke}
          strokeOpacity=".4"
          strokeWidth={strokeWidth}
        />
      </Svg>
      <Stack className={styles.children} align="center" justify="center">
        {!!children && children}
      </Stack>
    </View>
  );
};
